import { motion } from "framer-motion";
import { Activity, Zap, MapPin } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function ActivityStrain() {
  return (
    <section className="py-24 md:py-32 bg-zoku-bg" data-testid="activity-strain-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Copy (left) */}
        <motion.div {...reveal} className="lg:col-span-5">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zoku-orange mb-4">
            Activity & Strain
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Every rep, <br />
            every run,
            <br />
            <span className="font-editorial text-zoku-orange">one number.</span>
          </h2>
          <p className="mt-5 text-lg text-zoku-ink2">
            Track strain across lifts, rides, runs and yoga. Zoku reads your heart rate zones and
            gives you a single score to aim for — or rest from.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            {["Running", "Strength", "Cycling", "Yoga", "Padel", "Pilates", "HIIT", "Swim"].map(
              (a) => (
                <span
                  key={a}
                  className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-zoku-ink2 hover:border-zoku-orange hover:text-zoku-orange transition"
                >
                  {a}
                </span>
              )
            )}
          </div>
        </motion.div>

        {/* Cards (right) */}
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.1 }}
          className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          {/* Lifestyle image */}
          <div className="sm:row-span-2 rounded-[2rem] overflow-hidden relative min-h-[420px]">
            <img
              src="https://images.unsplash.com/photo-1750521280945-c533966f103c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHx3b3Jrb3V0JTIwbGlmZXN0eWxlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc3Njk3ODQ4fDA&ixlib=rb-4.1.0&q=85"
              alt="Gym portrait"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zoku-ink/70 to-transparent" />
            <div className="relative z-10 p-6 h-full flex flex-col justify-end text-white">
              <span className="text-xs uppercase tracking-[0.2em] opacity-80">Today's session</span>
              <h4 className="font-heading text-2xl font-bold mt-1">Lower body · 1h 24m</h4>
              <div className="mt-4 flex gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase opacity-70">Strain</div>
                  <div className="font-heading text-xl font-bold">16.8</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase opacity-70">kcal</div>
                  <div className="font-heading text-xl font-bold">517</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase opacity-70">Avg HR</div>
                  <div className="font-heading text-xl font-bold">142</div>
                </div>
              </div>
            </div>
          </div>

          {/* Strain gauge */}
          <div className="bg-white rounded-[2rem] p-6 border border-stone-100 shadow-widget">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-zoku-orange" />
              <span className="text-sm font-semibold text-zoku-ink">Weekly strain</span>
            </div>
            <div className="mt-4 flex items-end gap-1.5 h-24">
              {[40, 65, 30, 85, 55, 75, 45].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t-md relative group"
                  style={{
                    height: `${h}%`,
                    background: i === 3 ? "#FF6B00" : "#FFD4A3",
                  }}
                >
                  {i === 3 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-zoku-orange">
                      17.2
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between text-[10px] text-zoku-ink3 uppercase tracking-wider">
              {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
          </div>

          {/* Run map card */}
          <div className="bg-white rounded-[2rem] p-6 border border-stone-100 shadow-widget">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-semibold text-zoku-ink">Morning run</span>
              </div>
              <span className="text-xs text-zoku-ink3 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Indore
              </span>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-heading text-3xl font-bold">8.2</span>
              <span className="text-sm text-zoku-ink3">km</span>
              <span className="text-sm text-zoku-ink3 ml-auto">46m · 5:36/km</span>
            </div>
            <svg viewBox="0 0 200 60" className="mt-4 w-full h-14">
              <path
                d="M0,45 Q20,20 40,30 T80,25 T120,35 T160,15 T200,28"
                fill="none"
                stroke="#FF6B00"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M0,45 Q20,20 40,30 T80,25 T120,35 T160,15 T200,28 L200,60 L0,60 Z"
                fill="url(#runGrad)"
                opacity="0.3"
              />
              <defs>
                <linearGradient id="runGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF6B00" />
                  <stop offset="100%" stopColor="#FF6B00" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
